import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react';

import { SafeHtml } from '../../../../../components';
import { Accordion, IconButton } from '../../../../../compositions';
import { Product } from '../../../../../entities/@products/Product/Product';
import { trimToMaxLength } from '../../../../../helpers/string';
import { useDeviceWidth, useTrans } from '../../../../../hooks';
import { ProductTableInformation } from '../ProductTableInformation/ProductTableInformation';

import './ProductInformation.scss';

interface ProductInformationProps {
    product: Product;
    className?: string;
}

export const ProductInformation: FC<ProductInformationProps> = ({
    product,
    className = '',
}): ReactElement => {
    const trans = useTrans();

    const { isMobile } = useDeviceWidth();

    const maxCharactersForDescription = 250;

    const [description, setDescription] = useState<string>(product.description);
    const [descriptionShowMore, setDescriptionShowMore] = useState<boolean>(false);
    const descriptionIsTooLong = product.description.length >= maxCharactersForDescription;

    useEffect((): void => {
        if (descriptionIsTooLong) {
            const truncatedDescription = trimToMaxLength(description, maxCharactersForDescription);

            setDescription(truncatedDescription);
        }
    }, []);

    const handleShowMoreDescription = (): void => {
        if (descriptionShowMore) {
            const truncatedDescription = trimToMaxLength(description, maxCharactersForDescription);

            setDescription(truncatedDescription);
        } else {
            setDescription(product.description);
        }

        setDescriptionShowMore(!descriptionShowMore);
    };

    return (
        <section className={`product-information ${className}`}>
            <div className="product-information__main-inner">
                <div className="product-information__description-wrapper">
                    <h2 className="product-information__title">
                        {trans('containers.productDetailRenderer.description')}
                    </h2>

                    {product.description ? (
                        <SafeHtml
                            html={description}
                            className="product-information__description"
                        />
                    ) : (
                        <p className="product-information__description">
                            {trans('containers.productDetailRenderer.noDescriptionAvailable')}
                        </p>
                    )}

                    {descriptionIsTooLong && (
                        <IconButton
                            isSmall
                            icon={descriptionShowMore ? 'chevron-up' : 'chevron-down'}
                            text={descriptionShowMore
                                ? trans('common.showLess')
                                : trans('common.showMore')}
                            onClick={handleShowMoreDescription}
                            className="product-information__show-more-description-button"
                        />
                    )}
                </div>

                <aside className="product-information__details-wrapper">
                    {product.dimensionAttributes.length > 1 && (
                        <div className="product-information__dimensions-wrapper">
                            <Accordion
                                hasBorder={isMobile}
                                heading={trans('containers.productDetailRenderer.dimensions')}
                                className="product-information__title"
                                headingClassName="product-information__details-heading"
                            >
                                <ProductTableInformation attributes={product.dimensionAttributes} />
                            </Accordion>
                        </div>
                    )}

                    {product.attributes.length > 0 && (
                        <div className="product-information__specifications-wrapper">
                            <Accordion
                                hasBorder={isMobile}
                                heading={trans('containers.productDetailRenderer.specifications')}
                                className="product-information__title"
                                headingClassName="product-information__details-heading"
                            >
                                <ProductTableInformation attributes={product.attributes} />
                            </Accordion>
                        </div>
                    )}
                </aside>
            </div>
        </section>
    );
};
